import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import DashboardNavigation, { QUOTES_HREF } from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import TutorialModal from './TutorialModal'
import { useApi, isAuthError, isPolicySystemUnavailableApiError } from '../services/api'
import { usePortalAuth } from '../auth/authContexts'
import type { PolicyLink } from '../services/types'
import { POLICIES_LIST_CACHE_KEY } from '../utils/policiesListCache'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { usePolicySystemStatus } from '../context/PolicySystemStatusProvider'
import PolicySystemUnavailablePage from './PolicySystemUnavailablePage'
import PolicyBlockedMessageBody from './PolicyBlockedMessageBody'
import { PrimaryButton } from './ui'
import { policyIsUnsupportedVehicleType } from '../utils/policyType'
import { CLUB4X4_BENEFITS_HREF } from '../claims/contactLinks'
import { clientLogDebug, clientLogError } from '../utils/clientLog'

interface PolicyCardProps {
  policyRef: string
  regoNumber: string
  policyTypeLabel: string
  vehicleName: string
  /** Year, make and model (e.g. "2024 TOYOTA FORTUNER"); shown as main line when present */
  makeAndModel?: string | null
  /** From payload: yearBuilt, make, model (e.g. "2019", "TOYOTA", "HILUX") for short "2019 Toyota Hilux" display */
  yearBuilt?: string | null
  make?: string | null
  model?: string | null
  policyNumber?: string | null
  /** When true, hide self-service actions and show call-centre message */
  referralBlocked?: boolean
  referralMessage?: string | null
  /** When true, online policy edits are paused — CTA is View policy instead of Manage policy */
  policyUpdatesBlocked?: boolean
  /** When false, the Claims button is hidden (fifth wheeler / slide-on product types) */
  claimsSupported?: boolean
  onManagePolicy?: () => void
  onMakeClaim?: () => void
}

/** Title-case a word (e.g. "TOYOTA" → "Toyota"). */
function titleCase(s: string): string {
  const t = s.trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t
}

/** Build short "Year Make Model" from payload fields (e.g. "2019", "TOYOTA", "HILUX" → "2019 Toyota Hilux"). */
function shortVehicleFromPayload(yearBuilt: string | null | undefined, make: string | null | undefined, model: string | null | undefined): string {
  const y = yearBuilt?.trim()
  const ma = make?.trim()
  const mo = model?.trim()
  if (!y && !ma && !mo) return ''
  const parts = [y || '', ma ? titleCase(ma) : '', mo ? titleCase(mo) : ''].filter(Boolean)
  return parts.join(' ')
}

/** Derive short "Year Make Model" from full vehicle string when payload fields missing (e.g. "2019 TOYOTA HILUX 6M..." → "2019 Toyota Hilux"). */
function shortVehicleDisplay(full: string): string {
  const trimmed = full.trim()
  if (!trimmed) return trimmed
  const tokens = trimmed.split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return ''
  const take = tokens.slice(0, 3)
  const formatted = take.map((t, i) =>
    i === 0 && /^\d{4}$/.test(t) ? t : titleCase(t)
  )
  return formatted.join(' ')
}

function PolicyCard({
  policyRef,
  regoNumber,
  policyTypeLabel,
  vehicleName,
  makeAndModel,
  yearBuilt,
  make,
  model,
  policyNumber,
  onManagePolicy,
  onMakeClaim,
  referralBlocked,
  referralMessage,
  policyUpdatesBlocked,
  claimsSupported = true,
}: PolicyCardProps) {
  const [showVehicleDetails, setShowVehicleDetails] = useState(false)
  const fullVehicleName = vehicleName?.trim() || ''
  const fromPayload = shortVehicleFromPayload(yearBuilt ?? undefined, make ?? undefined, model ?? undefined)
  const rawMainLine = fromPayload
    || (makeAndModel?.trim() ? shortVehicleDisplay(makeAndModel.trim()) : '')
    || shortVehicleDisplay(fullVehicleName)
    || fullVehicleName
  const mainVehicleLine = rawMainLine
    .split(/\s+/)
    .filter(Boolean)
    .map((t, i) => (i === 0 && /^\d{4}$/.test(t) ? t : titleCase(t)))
    .join(' ')
  const hasExtraDetails = fullVehicleName.length > 0
    && fullVehicleName.toLowerCase().replace(/\s+/g, ' ') !== mainVehicleLine.toLowerCase().replace(/\s+/g, ' ')
  return (
    <div data-testid={`policy-card-${policyRef}`} className="bg-background-primary border border-border-primary border-solid flex flex-col md:flex-row md:items-center md:justify-between isolate relative rounded-2xl shadow-xs w-full px-[20px] py-[20px] gap-[16px] md:gap-[20px]">
      {/* Vehicle Info */}
      <div className="flex flex-col gap-[12px] items-start">
        {/* Rego + policy type badges */}
        <div className="content-start flex flex-wrap gap-[8px] items-center">
          <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] rounded-xs">
            <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
              {regoNumber}
            </p>
          </div>
          <span className="font-body font-medium text-sm leading-sm text-text-primary-900 bg-background-tertiary px-[8px] py-[4px] rounded-xs">
            {policyTypeLabel}
          </span>
        </div>

        {/* Vehicle: year/make/model as main line; optional "Show details" for full name */}
        <div className="flex flex-col gap-[4px] items-start">
          <p className="font-body font-semibold text-md leading-md text-text-primary-900">
            {mainVehicleLine}
          </p>
          {policyNumber && (
            <p className="font-body font-regular text-sm leading-sm text-text-secondary-700">
              Policy no. {policyNumber}
            </p>
          )}
          {hasExtraDetails && (
            <>
              <button
                type="button"
                onClick={() => setShowVehicleDetails((prev) => !prev)}
                className="font-body font-medium text-sm text-text-tertiary-600 hover:text-text-secondary-700 underline focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-border-primary rounded px-0 py-0"
              >
                {showVehicleDetails ? 'Hide details' : 'Show details'}
              </button>
              {showVehicleDetails && (
                <p className="font-body font-regular text-sm text-text-secondary-700">
                  {fullVehicleName}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-[8px] items-stretch sm:items-center">
        {referralBlocked ? (
          <PolicyBlockedMessageBody
            message={
              referralMessage?.trim()
                ? referralMessage
                : 'This policy is with our underwriting team. Please call the Club 4X4 call centre on 1300 296 296. Online actions are not available until that is complete.'
            }
            className="font-body font-regular text-sm text-text-secondary-700 max-w-[320px]"
          />
        ) : (
          <>
            <button
              type="button"
              onClick={onManagePolicy}
              className="bg-background-brand-solid flex gap-[6px] items-center justify-center px-[20px] py-[10px] relative rounded-lg shadow-xs whitespace-nowrap"
            >
              <span className="font-body font-semibold text-sm text-text-white">
                {policyUpdatesBlocked ? 'View policy' : 'Manage policy'}
              </span>
            </button>
            {claimsSupported && (
              <PrimaryButton
                type="button"
                onClick={onMakeClaim}
                size="md"
                className="!rounded-lg !text-sm !leading-sm !bg-[#F3DC6B] !text-text-primary-900"
              >
                Claims
              </PrimaryButton>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface PromoCardProps {
  title: string
  description: string
  buttonText: string
  imagePlaceholder?: string
  buttonHref?: string
  onButtonClick?: () => void
}

function PromoCard({
  title,
  description,
  buttonText,
  imagePlaceholder,
  buttonHref,
  onButtonClick,
}: PromoCardProps) {
  return (
    <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center relative rounded-2xl shadow-xs w-full overflow-hidden">
      {/* Image - edge to edge with rounded top corners only */}
      <div className="w-full aspect-[4/3] bg-background-tertiary flex items-center justify-center">
        {imagePlaceholder ? (
          <img src={imagePlaceholder} alt={title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-text-tertiary-600 text-sm">Image placeholder</span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-[8px] items-center text-center w-full px-[16px] pt-[16px]">
        <p className="font-body font-semibold text-md leading-md text-text-primary-900">
          {title}
        </p>
        <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
          {description}
        </p>
      </div>

      {/* Button with external link icon */}
      <div className="w-full px-[16px] pb-[16px] pt-[16px]">
        {buttonHref ? (
          <a
            href={buttonHref}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-background-primary border border-border-primary border-solid flex gap-[6px] items-center justify-center overflow-clip px-[16px] py-[10px] relative rounded-md shadow-xs w-full"
          >
            <span className="font-body font-semibold text-md text-text-secondary-700">
              {buttonText}
            </span>
            <svg className="w-4 h-4 text-text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        ) : (
          <button
            type="button"
            onClick={onButtonClick}
            className="bg-background-primary border border-border-primary border-solid flex gap-[6px] items-center justify-center overflow-clip px-[16px] py-[10px] relative rounded-md shadow-xs w-full"
          >
            <span className="font-body font-semibold text-md text-text-secondary-700">
              {buttonText}
            </span>
            <svg className="w-4 h-4 text-text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = usePortalAuth()
  const logoutRef = useRef(logout)
  logoutRef.current = logout
  const api = useApi()
  const [showTutorial, setShowTutorial] = useState(false)
  const [tutorialStep, setTutorialStep] = useState(1)
  const [policies, setPolicies] = useState<PolicyLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [policyOutageCorrelationId, setPolicyOutageCorrelationId] = useState<string | null>(null)
  const [lastFetchError, setLastFetchError] = useState<unknown>(null)
  useScrollToTopOnErrorSignal(error)
  const policySystem = usePolicySystemStatus()
  const [firstName, setFirstName] = useState<string>('')
  const [refreshTrigger, setRefreshTrigger] = useState(0) // Trigger for manual refresh
  const TOTAL_TUTORIAL_STEPS = 3

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    // Dev-only: Check for debug mode via URL parameter (e.g., ?debug=tutorial)
    if (import.meta.env.DEV) {
      const urlParams = new URLSearchParams(window.location.search)
      const debugMode = urlParams.get('debug')
      
      if (debugMode === 'tutorial') {
        // Force show tutorial for testing, regardless of localStorage
        setShowTutorial(true)
        setTutorialStep(1)
        return
      }
    }
    
    // Normal behavior: check if user has completed tutorial
    const hasCompletedTutorial = localStorage.getItem('club4x4_tutorial_completed')
    if (!hasCompletedTutorial) {
      setShowTutorial(true)
    }
  }, [])

  // Utility function to cache policies
  const cachePolicies = (data: { policies: any[]; firstName: string }) => {
    try {
      sessionStorage.setItem(POLICIES_LIST_CACHE_KEY, JSON.stringify(data))
    } catch (e) {
      clientLogError('Error caching policies.', e)
    }
  }

  // Utility function to clear cache (for refresh)
  const clearCache = () => {
    try {
      sessionStorage.removeItem(POLICIES_LIST_CACHE_KEY)
    } catch (e) {
      clientLogError('Error clearing cache.', e)
    }
  }

  // Listen for refresh event from navigation dropdown
  useEffect(() => {
    const handleRefresh = () => {
      clearCache()
      setRefreshTrigger(prev => prev + 1) // Trigger re-fetch
    }

    window.addEventListener('club4x4:refreshDashboard', handleRefresh)
    return () => {
      window.removeEventListener('club4x4:refreshDashboard', handleRefresh)
    }
  }, [])

  // Fetch policies from API - with caching support
  useEffect(() => {
    let cancelled = false
    const MAX_RETRIES = 1 // Only retry once

    // Use bootstrap data when we just landed from login (skip redundant fetch)
    const state = location.state as { bootstrapPolicies?: { policies: PolicyLink[]; firstName?: string } } | null
    const bootstrap = state?.bootstrapPolicies
    if (bootstrap?.policies && Array.isArray(bootstrap.policies) && bootstrap.policies.length > 0) {
      const policyList = bootstrap.policies as PolicyLink[]
      setFirstName(bootstrap.firstName ?? '')
      setPolicies(policyList)
      cachePolicies({ policies: policyList, firstName: bootstrap.firstName || '' })
      setLoading(false)
      setError(null)
      // Clear state so Back / other navigations don't reuse stale bootstrap data
      navigate(location.pathname, { replace: true, state: {} })
      return
    }

    const fetchPolicies = async (retryCount = 0) => {
      // Check if user has already visited confirm-details (bypassed onboarding)
      const hasVisitedConfirmDetails = sessionStorage.getItem('club4x4_visited_confirm_details') === 'true';

      // When dashboard mounts we always query the API (never use cached data) so we show
      // current ISSUED policies from the server (ACCEPTABLE quotes are not listed here).
      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
          setPolicyOutageCorrelationId(null);
          setLastFetchError(null);
        }
        
        const response = await api.getPolicies()
        
        // Don't update state if component unmounted
        if (cancelled) return
        
        // Always set firstName from API response (derived from policy payload on each refresh)
        setFirstName(response.firstName ?? '')
        
        // Check if user has no policies - redirect to onboarding only if they haven't visited it
        if (!response.policies || response.policies.length === 0) {
          if (!cancelled) {
            setLoading(false);
            clearCache(); // Clear stale cache so we don't show old policy list
            // Only redirect if user hasn't bypassed onboarding
            if (!hasVisitedConfirmDetails) {
              navigate('/confirm-details', { replace: true });
            } else {
              setPolicies([]);
            }
          }
          // Reset refresh trigger after handling empty response
          if (refreshTrigger > 0) {
            setRefreshTrigger(0)
          }
          return;
        }
        
        // Store full PolicyLink[] so Manage policy can use it without a second API call
        const policyList = response.policies as PolicyLink[];
        
        if (!cancelled) {
          setPolicies(policyList);
          // Cache full policies for instant load when returning to dashboard
          cachePolicies({
            policies: policyList,
            firstName: response.firstName || ''
          })
          // Reset refresh trigger after successful refresh
          if (refreshTrigger > 0) {
            setRefreshTrigger(0)
          }
        }
      } catch (err) {
        // Don't update state if component unmounted
        if (cancelled) return
        
        // Session expired or denied (401/403): sign out so user can sign in again (fixes iOS Safari stuck on "Error loading policies")
        if (isAuthError(err)) {
          try {
            await logoutRef.current()
          } catch (logoutErr) {
            clientLogError('[Dashboard] Logout failed.', logoutErr)
            if (!cancelled) {
              clearCache()
              setError(err instanceof Error ? err.message : 'Session expired. Please sign in again.')
              setPolicies([])
            }
          }
          return
        }
        
        const isTimeout = err instanceof Error && (
          err.message.includes('timeout') || 
          err.message.includes('timed out') ||
          err.message.includes('AbortError') ||
          err.name === 'AbortError' ||
          err.name === 'TimeoutError'
        )
        
        clientLogError(`Error fetching policies (attempt ${retryCount + 1}).`, err);
        
        // Retry on timeout or network errors (but not on auth errors)
        if (retryCount < MAX_RETRIES && isTimeout) {
          clientLogDebug(`Retrying after timeout (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchPolicies(retryCount + 1);
        }
        
        // Show error after max retries or non-retryable errors; clear cache so we don't show stale data
        if (!cancelled) {
          clearCache();
          setLastFetchError(err);
          if (isPolicySystemUnavailableApiError(err)) {
            const apiBody = (err as Error & { apiBody?: { correlationId?: string; message?: string } }).apiBody;
            setPolicyOutageCorrelationId(apiBody?.correlationId ?? null);
            setError(apiBody?.message ?? (err instanceof Error ? err.message : 'Failed to load policies'));
          } else {
            setError(err instanceof Error ? err.message : 'Failed to load policies');
          }
          setPolicies([]);
          // Reset refresh trigger even on error
          if (refreshTrigger > 0) {
            setRefreshTrigger(0)
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPolicies()

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true
    }
  }, [navigate, api, refreshTrigger]) // logout via ref inside effect (avoids re-fetch loops if callback identity flickers)

  const handleTutorialNext = () => {
    if (tutorialStep < TOTAL_TUTORIAL_STEPS) {
      setTutorialStep(tutorialStep + 1)
    } else {
      // Completed all steps
      localStorage.setItem('club4x4_tutorial_completed', 'true')
      setShowTutorial(false)
    }
  }

  const handleTutorialSkip = () => {
    localStorage.setItem('club4x4_tutorial_completed', 'true')
    setShowTutorial(false)
  }

  const getTutorialContent = () => {
    switch (tutorialStep) {
      case 1:
        return {
          title: 'Welcome to your Club 4X4 dashboard',
          description: "We're glad to have you onboard. Here are some quick tips to get you up and running.",
          imageUrl: '/onboarding-1.png',
        }
      case 2:
        return {
          title: 'Manage your policies',
          description: 'View all your policy details, update your information, and keep track of renewal dates - all in one place.',
          imageUrl: '/onboarding-2.png',
        }
      case 3:
        return {
          title: "Make a claim",
          description: "If something goes wrong, you can start your claim right here by submitting a notification of loss. We'll take care of the rest.",
          imageUrl: '/onboarding-3.png',
        }
      default:
        return {
          title: '',
          description: '',
          imageUrl: undefined,
        }
    }
  }

  const handleManagePolicy = (policyRef: string) => {
    clientLogDebug('Manage policy:', policyRef)
    const policy = policies.find((p) => p.policyRef === policyRef)
    navigate(`/policy/${encodeURIComponent(policyRef)}`, {
      state: policy ? { policy } : undefined,
    })
  }

  const handleMakeClaim = (policyRef: string) => {
    const policy = policies.find((p) => p.policyRef === policyRef)
    navigate(`/claims?policyRef=${encodeURIComponent(policyRef)}`, {
      state: policy ? { policy } : undefined,
    })
  }

  const showPolicySystemUnavailablePage =
    !loading &&
    policies.length === 0 &&
    (isPolicySystemUnavailableApiError(lastFetchError) ||
      (policySystem.isUnavailable && lastFetchError != null));

  if (showPolicySystemUnavailablePage) {
    return (
      <PolicySystemUnavailablePage
        correlationId={policyOutageCorrelationId ?? undefined}
        onTryAgain={async () => {
          setRefreshTrigger((value) => value + 1);
        }}
      />
    );
  }

  return (
    <div className="bg-background-tertiary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start">
      {/* Header Section */}
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] md:py-[48px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[16px] md:gap-[24px] items-start relative shrink-0 w-full">
            <div className="flex flex-col font-semibold gap-[12px] items-start relative shrink-0 w-full">
              <p className="font-body font-semibold leading-sm relative shrink-0 text-foreground-brand-primary-alt text-sm md:text-md w-full">
                DASHBOARD
              </p>
              {firstName ? (
                <h1 className="m-0 font-display leading-[38px] md:leading-[44px] relative shrink-0 text-text-primary-on-brand text-display-sm md:text-display-md tracking-[-0.72px] w-full">
                  Welcome, {firstName}
                </h1>
              ) : (
                <h1 className="sr-only">Dashboard</h1>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col gap-[32px] md:gap-[48px] items-start px-[16px] md:px-[32px] py-[40px] md:py-[96px] relative w-full max-w-container-desktop mx-auto">

        {/* Policies Section */}
        <div className="flex flex-col gap-[24px] items-start relative w-full">
          <h2 className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
            Policies
          </h2>
          {loading ? (
            <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[48px] relative rounded-2xl shadow-xs w-full">
              <LoadingIcon size="medium" message="Loading policies..." />
            </div>
          ) : error ? (
            <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[40px] relative rounded-2xl shadow-xs w-full">
              <p className="font-body font-semibold text-md leading-md text-text-primary-900 mb-2">
                Error loading policies
              </p>
              <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                {error}
              </p>
            </div>
          ) : policies.length === 0 ? (
            <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[40px] relative rounded-2xl shadow-xs w-full">
              <p className="font-body font-semibold text-md leading-md text-text-primary-900">
                No policies found
              </p>
              <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 max-w-[320px] text-center mt-2">
                You don't have any linked policies yet. Contact support to link your policies.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-[12px] items-start relative w-full">
              {policies.map((policy, index) => {
                const regoFallback = policy.policyNumber || policy.policyRef;
                const regoNumber = policy.registrationNumber || regoFallback;
                const policyTypeLabel = policy.has4X4Vehicle === false
                  ? (policy.non4x4InsuredAssetLabel?.trim() || 'Non-4x4 policy')
                  : '4x4';
                const vehicleName = policy.vehicleName || `Policy ${policy.policyRef}`;
                return (
                  <PolicyCard
                    key={policy.policyRef || index}
                    policyRef={policy.policyRef}
                    regoNumber={regoNumber}
                    policyTypeLabel={policyTypeLabel}
                    vehicleName={vehicleName}
                    makeAndModel={policy.makeAndModel ?? undefined}
                    yearBuilt={policy.yearBuilt ?? undefined}
                    make={policy.make ?? undefined}
                    model={policy.model ?? undefined}
                    policyNumber={policy.policyNumber ?? null}
                    referralBlocked={policy.referralBlocked === true}
                    referralMessage={policy.referralMessage ?? null}
                    policyUpdatesBlocked={policy.policyUpdatesBlocked === true}
                    claimsSupported={!policyIsUnsupportedVehicleType(policy)}
                    onManagePolicy={() => handleManagePolicy(policy.policyRef)}
                    onMakeClaim={() => handleMakeClaim(policy.policyRef)}
                  />
                );
              })}
            </div>
          )}

          {/* Missing Policy Link */}
          <div className="flex justify-end w-full">
            <button
              type="button"
              onClick={() => navigate('/confirm-details', { state: { linkAdditionalPolicy: true } })}
              className="flex items-center gap-[6px] text-text-tertiary-600 hover:text-text-secondary-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
              <span className="font-body font-medium text-sm">Missing a policy?</span>
            </button>
          </div>
        </div>

        {/* Promotional Cards */}
        <div className="flex flex-col md:flex-row gap-[16px] items-start relative w-full">
          <PromoCard
            title="Cover another vehicle"
            description="Insure your 4X4, 5th wheeler, camper trailer, caravan or slide-on, all in the one spot."
            buttonText="Get a quote"
            imagePlaceholder="/promo-cover-vehicle-small.jpg"
            buttonHref={QUOTES_HREF}
          />
          <PromoCard
            title="Discounts & vouchers"
            description="Our customers can access a number of exclusive offers from our partners."
            buttonText="Learn more"
            imagePlaceholder="/promo-discounts-small.jpg"
            buttonHref={CLUB4X4_BENEFITS_HREF}
          />
        </div>
      </div>
      </main>

      <Footer />

      {/* Tutorial Modal */}
      {showTutorial && (
        <TutorialModal
          currentStep={tutorialStep}
          totalSteps={TOTAL_TUTORIAL_STEPS}
          onNext={handleTutorialNext}
          onSkip={handleTutorialSkip}
          title={getTutorialContent().title}
          description={getTutorialContent().description}
          imageUrl={getTutorialContent().imageUrl}
        />
      )}
    </div>
  )
}

